import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog"]

  connect() {
    this.handleCancel = this.handleCancel.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.previouslyFocusedElement = null

    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener("cancel", this.handleCancel)
    }
  }

  disconnect() {
    if (this.hasDialogTarget) {
      this.dialogTarget.removeEventListener("cancel", this.handleCancel)
    }

    document.removeEventListener("keydown", this.handleKeydown)
  }

  open(event) {
    event?.preventDefault()

    if (!this.hasDialogTarget || this.dialogTarget.open) return

    this.previouslyFocusedElement = document.activeElement

    if (typeof this.dialogTarget.showModal === "function") {
      this.dialogTarget.showModal()
    } else {
      this.dialogTarget.setAttribute("open", "")
    }

    document.addEventListener("keydown", this.handleKeydown)
    this.focusInitialElement()
  }

  close(event) {
    event?.preventDefault()
    this.closeDialog()
  }

  closeOnBackdrop(event) {
    if (event.target !== this.dialogTarget) return

    this.closeDialog()
  }

  handleCancel(event) {
    event.preventDefault()
    this.closeDialog()
  }

  handleKeydown(event) {
    if (!this.dialogTarget.open) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.closeDialog()
      return
    }

    if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  closeDialog() {
    if (!this.hasDialogTarget || !this.dialogTarget.open) return

    document.removeEventListener("keydown", this.handleKeydown)

    if (typeof this.dialogTarget.close === "function") {
      this.dialogTarget.close()
    } else {
      this.dialogTarget.removeAttribute("open")
    }

    this.restoreFocus()
  }

  focusInitialElement() {
    const focusableElements = this.focusableElements
    const closeButton = this.dialogTarget.querySelector("[data-modal-close]")
    const initialElement = closeButton || focusableElements[0] || this.dialogTarget

    initialElement.focus()
  }

  trapFocus(event) {
    const focusableElements = this.focusableElements

    if (focusableElements.length === 0) {
      event.preventDefault()
      this.dialogTarget.focus()
      return
    }

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  restoreFocus() {
    if (this.previouslyFocusedElement instanceof HTMLElement) {
      this.previouslyFocusedElement.focus()
    }

    this.previouslyFocusedElement = null
  }

  get focusableElements() {
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "textarea:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(",")

    return Array.from(this.dialogTarget.querySelectorAll(selector))
      .filter((element) => element instanceof HTMLElement && !element.hasAttribute("hidden"))
  }
}
